// Eigenes Kontextmenü statt des Browser-Menüs – in der ganzen App (einmal in main.tsx eingehängt).
// Ein Listener auf document verhindert IMMER das Browser-Menü. Bereiche hängen ihre Einträge über
// onContextMenu={(e) => menueOeffnen(e, eintraege)} an das Ereignis (der innerste Bereich gewinnt).
// Ohne eigene Einträge: Textfelder → Ausschneiden/Kopieren/Einfügen/Alles markieren, Links → neuer Tab/Link kopieren,
// sonst das allgemeine Menü (Zurück, Vorwärts, Neu laden, Link kopieren, Suchen). Markierter Text: „Kopieren“ zuerst.
// Tastatur: Shift+F10 oder Kontextmenü-Taste öffnet das Menü des fokussierten Elements; Pfeile, Pos1/Ende, Enter, Esc,
// → öffnet ein Untermenü, ← zurück. Schließt bei Esc, Klick daneben, Scrollen, Fensterwechsel und nach der Auswahl.
import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent as RMouseEvent } from 'react';
import { Icon } from './Icon';
import { toast } from './toast';
import { zumKopieren } from './frage';
import {
  allgemeinesMenue, erster, kennung, letzter, linkMenue, naechster, position, textfeldMenue, waehlbar, type Eintrag, type Zeile,
} from '../kontextmenue';
import '../styles/kontextmenue.css';

export type { Zeile, Eintrag } from '../kontextmenue';

const MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);

// ---------------------------------------------------------------- Einträge an das Ereignis hängen

const ausstehend = new WeakMap<Event, Zeile[]>();
const perTastaturSet = new WeakSet<Event>();

/** Über Shift+F10 / Kontextmenü-Taste ausgelöst (Ziel ist das fokussierte Element, keine Mausposition)? */
export const perTastatur = (e: RMouseEvent | MouseEvent) => perTastaturSet.has('nativeEvent' in e ? e.nativeEvent : e);

/**
 * Eigene Einträge für dieses Kontextmenü-Ereignis (in onContextMenu aufrufen). Der innerste Bereich gewinnt;
 * in Textfeldern gilt immer das Textmenü.
 */
export function menueOeffnen(e: RMouseEvent | MouseEvent, zeilen: Zeile[]) {
  const ev = 'nativeEvent' in e ? e.nativeEvent : e;
  if (ausstehend.has(ev) || textfeld(ev.target as Element | null)) return;
  ausstehend.set(ev, zeilen);
}

// ---------------------------------------------------------------- Hilfen für die Aktionen

/** Absolute Adresse zu einem Pfad der App. */
export const adresse = (pfad: string) => new URL(pfad, location.origin).href;

/** Text in die Zwischenablage; gesperrt: zum Kopieren anzeigen. */
export async function kopieren(text: string, meldung = 'Link kopiert.') {
  try {
    await navigator.clipboard.writeText(text);
    toast(meldung);
  } catch {
    await zumKopieren('Die Zwischenablage ist gesperrt – bitte von hier kopieren:', text);
  }
}

export const neuerTab = (url: string) => void window.open(url, '_blank', 'noopener');

/** Wartet (bis 3 s) auf ein Element, z. B. im gerade geöffneten Detailbereich. */
export function wartenAuf(selektor: string, ms = 3000): Promise<HTMLElement | null> {
  const ende = Date.now() + ms;
  return new Promise((fertig) => {
    const schauen = () => {
      const el = document.querySelector<HTMLElement>(selektor);
      if (el) fertig(el);
      else if (Date.now() > ende) fertig(null);
      else window.setTimeout(schauen, 60);
    };
    // erst nach dem nächsten Zeichnen – sonst träfe man noch den alten Inhalt
    window.setTimeout(schauen, 120);
  });
}

/** Feld im Detailbereich zeigen und fokussieren (z. B. „Aufgabe anlegen“, „Unteraufgabe hinzufügen“). */
export async function feldFokussieren(selektor: string, fehlt = 'Das Feld ist gerade nicht verfügbar.') {
  const el = await wartenAuf(selektor);
  if (!el) { toast(fehlt, true); return; }
  el.scrollIntoView({ block: 'center', behavior: 'smooth' });
  el.focus();
}

// ---------------------------------------------------------------- Textfelder

const OHNE_TEXT = /^(checkbox|radio|button|submit|reset|file|range|color|image|hidden)$/i;
type Feld = HTMLInputElement | HTMLTextAreaElement | HTMLElement;

function textfeld(el: Element | null): Feld | null {
  if (!el || !(el instanceof Element)) return null;
  const f = el.closest('input, textarea, [contenteditable]') as Feld | null;
  if (!f) return null;
  if (f instanceof HTMLInputElement) return OHNE_TEXT.test(f.type) ? null : f;
  if (f instanceof HTMLTextAreaElement) return f;
  return f.isContentEditable ? f : null;
}

const istEingabe = (f: Feld): f is HTMLInputElement | HTMLTextAreaElement => f instanceof HTMLInputElement || f instanceof HTMLTextAreaElement;

function textfeldEintraege(f: Feld): Zeile[] {
  const sel = window.getSelection();
  let start: number | null = null, ende: number | null = null, auswahl = '';
  let bereich: Range | null = null;
  if (istEingabe(f)) {
    try { start = f.selectionStart; ende = f.selectionEnd; } catch { /* z. B. type=date */ }
    auswahl = start !== null && ende !== null ? f.value.slice(start, ende) : (sel?.toString() ?? '');
  } else if (sel && sel.rangeCount && f.contains(sel.anchorNode)) {
    bereich = sel.getRangeAt(0).cloneRange();
    auswahl = sel.toString();
  }
  const schreibbar = istEingabe(f) ? !f.readOnly && !f.disabled : f.isContentEditable;
  const wiederherstellen = () => {
    f.focus();
    if (istEingabe(f)) { if (start !== null && ende !== null) try { f.setSelectionRange(start, ende); } catch { /* egal */ } }
    else if (bereich && sel) { sel.removeAllRanges(); sel.addRange(bereich); }
  };
  // Einfügen/Löschen wie beim Tippen (mit Rückgängig und input-Ereignis für React)
  const schreiben = (text: string) => {
    wiederherstellen();
    if (document.execCommand(text ? 'insertText' : 'delete', false, text)) return;
    if (istEingabe(f) && start !== null && ende !== null) {
      f.setRangeText(text, start, ende, 'end');
      f.dispatchEvent(new Event('input', { bubbles: true }));
    }
  };
  const inAblage = async (text: string) => {
    try { await navigator.clipboard.writeText(text); return true; } catch { /* Fallback */ }
    wiederherstellen();
    if (document.execCommand('copy')) return true;
    toast(`Kopieren mit ${MAC ? '⌘' : 'Strg'}+C – der Browser erlaubt den Zugriff auf die Zwischenablage hier nicht.`, true);
    return false;
  };
  return textfeldMenue(
    { auswahl: !!auswahl, schreibbar, leer: istEingabe(f) ? !f.value : !f.textContent, geheim: f instanceof HTMLInputElement && f.type === 'password', mac: MAC },
    {
      ausschneiden: async () => { if (await inAblage(auswahl)) schreiben(''); },
      kopieren: async () => { await inAblage(auswahl); wiederherstellen(); },
      einfuegen: async () => {
        let text: string;
        try { text = await navigator.clipboard.readText(); } catch {
          wiederherstellen();
          toast(`Einfügen mit ${MAC ? '⌘' : 'Strg'}+V – der Browser erlaubt das Lesen der Zwischenablage hier nicht.`, true);
          return;
        }
        schreiben(text);
      },
      allesMarkieren: () => {
        f.focus();
        if (istEingabe(f)) f.select();
        else if (sel) { const r = document.createRange(); r.selectNodeContents(f); sel.removeAllRanges(); sel.addRange(r); }
      },
    },
  );
}

// ---------------------------------------------------------------- Links, markierter Text, allgemeines Menü

function linkVon(el: Element | null): HTMLAnchorElement | null {
  const a = el?.closest?.('a[href]') as HTMLAnchorElement | null;
  const h = a?.getAttribute('href') ?? '';
  return a && h !== '#' && !h.startsWith('javascript:') ? a : null;
}

function allgemeineEintraege(): Zeile[] {
  const nav = (window as unknown as { navigation?: { canGoBack?: boolean; canGoForward?: boolean } }).navigation;
  const suche = document.querySelector<HTMLInputElement>('[data-testid=search]');
  return allgemeinesMenue(
    { zurueck: nav?.canGoBack ?? history.length > 1, vorwaerts: nav?.canGoForward ?? true, suche: !!suche, mac: MAC },
    {
      zurueck: () => history.back(),
      vorwaerts: () => history.forward(),
      neuLaden: () => location.reload(),
      linkKopieren: () => kopieren(location.href),
      suchen: () => {
        // Handy: Suchzeile erst aufklappen
        if (suche && !suche.offsetParent) document.querySelector<HTMLElement>('[data-testid=kopf-suche]')?.click();
        window.setTimeout(() => document.querySelector<HTMLInputElement>('[data-testid=search]')?.focus(), 30);
      },
    },
  );
}

// ---------------------------------------------------------------- Anzeige

interface Offen { x: number; y: number; zeilen: Zeile[]; nr: number }
interface Ebene { titel: string | null; zeilen: Zeile[] }

/** Einmal einhängen (main.tsx). */
export function KontextmenueHost() {
  const [offen, setOffen] = useState<Offen | null>(null);
  const zaehler = useRef(0);
  const letzteTastatur = useRef(0);

  useEffect(() => {
    const beimMenue = (ev: MouseEvent) => {
      ev.preventDefault(); // nie das Browser-Menü
      if (ev.target instanceof Element && ev.target.closest('.kontextmenue')) return;
      const tastatur = perTastaturSet.has(ev);
      // Kontextmenü-Taste: manche Browser schicken nach unserem Ereignis noch ein eigenes – nicht doppelt öffnen
      if (!tastatur && ev.isTrusted && Date.now() - letzteTastatur.current < 600) return;
      const ziel = ev.target instanceof Element ? ev.target : null;
      let zeilen: Zeile[] | undefined;
      const feld = textfeld(ziel);
      if (feld) zeilen = textfeldEintraege(feld);
      else {
        zeilen = ausstehend.get(ev);
        const link = linkVon(ziel);
        if (!zeilen && link) zeilen = linkMenue({ neuerTab: () => neuerTab(link.href), kopieren: () => kopieren(link.href) });
        zeilen ??= allgemeineEintraege();
        const markiert = window.getSelection()?.toString() ?? '';
        if (markiert.trim()) zeilen = [{ text: 'Kopieren', icon: 'copy', kuerzel: MAC ? '⌘C' : 'Strg+C', testid: 'km-auswahl-kopieren', aktion: () => kopieren(markiert, 'Kopiert.') }, '-', ...zeilen];
      }
      let x = ev.clientX, y = ev.clientY;
      if (tastatur && ziel) { const r = ziel.getBoundingClientRect(); x = r.left + Math.min(24, r.width / 2); y = r.top + Math.min(r.height, 40); }
      setOffen({ x, y, zeilen, nr: ++zaehler.current });
    };
    const taste = (e: KeyboardEvent) => {
      if (!(e.key === 'ContextMenu' || (e.shiftKey && e.key === 'F10'))) return;
      e.preventDefault();
      letzteTastatur.current = Date.now();
      const el = (document.activeElement as HTMLElement | null) ?? document.body;
      const r = el.getBoundingClientRect();
      const ev = new MouseEvent('contextmenu', { bubbles: true, cancelable: true, clientX: r.left, clientY: r.bottom, button: 2 });
      perTastaturSet.add(ev);
      el.dispatchEvent(ev);
    };
    document.addEventListener('contextmenu', beimMenue);
    window.addEventListener('keydown', taste, true);
    return () => { document.removeEventListener('contextmenu', beimMenue); window.removeEventListener('keydown', taste, true); };
  }, []);

  if (!offen) return null;
  return <Menue key={offen.nr} offen={offen} schliessen={() => setOffen(null)} />;
}

function Menue({ offen, schliessen }: { offen: Offen; schliessen: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [stapel, setStapel] = useState<Ebene[]>([{ titel: null, zeilen: offen.zeilen }]);
  const ebene = stapel[stapel.length - 1];
  // Untermenü: erste Zeile „‹ Titel“ führt zurück
  const zeilen: Zeile[] = ebene.titel ? [{ text: ebene.titel, icon: 'back', testid: 'km-zurueck' }, '-', ...ebene.zeilen] : ebene.zeilen;
  const [aktiv, setAktiv] = useState(() => erster(zeilen));
  const [pos, setPos] = useState<{ left: number; top: number; maxHoehe: number } | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.maxHeight = '';
    const r = el.getBoundingClientRect();
    setPos(position(offen.x, offen.y, r.width, el.scrollHeight, window.innerWidth, window.innerHeight));
  }, [stapel]);

  const zurueck = () => { if (stapel.length > 1) { const s = stapel.slice(0, -1); setStapel(s); setAktiv(erster(s[s.length - 1].zeilen)); } };
  const waehlen = (i: number) => {
    const z = zeilen[i];
    if (!waehlbar(z)) return;
    if (ebene.titel && i === 0) { zurueck(); return; }
    if (z.unter) {
      setStapel([...stapel, { titel: z.text, zeilen: z.unter }]);
      setAktiv(erster(z.unter) + 2);
      return;
    }
    schliessen();
    try {
      void Promise.resolve(z.aktion?.()).catch((e) => toast((e as Error).message, true));
    } catch (e) { toast((e as Error).message, true); }
  };

  // Tastatur, Klick daneben, Scrollen, Fensterwechsel
  const aktivRef = useRef(aktiv);
  aktivRef.current = aktiv;
  const waehlenRef = useRef(waehlen);
  waehlenRef.current = waehlen;
  const zeilenRef = useRef(zeilen);
  zeilenRef.current = zeilen;
  const zurueckRef = useRef(zurueck);
  zurueckRef.current = zurueck;
  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      const z = zeilenRef.current;
      const stopp = () => { e.preventDefault(); e.stopPropagation(); };
      switch (e.key) {
        case 'Escape': stopp(); schliessen(); return;
        case 'ArrowDown': stopp(); setAktiv(naechster(z, aktivRef.current, 1)); return;
        case 'ArrowUp': stopp(); setAktiv(naechster(z, aktivRef.current, -1)); return;
        case 'Home': stopp(); setAktiv(erster(z)); return;
        case 'End': stopp(); setAktiv(letzter(z)); return;
        case 'Enter': case ' ': stopp(); if (aktivRef.current >= 0) waehlenRef.current(aktivRef.current); return;
        case 'ArrowRight': { const x = z[aktivRef.current]; if (waehlbar(x) && x.unter) { stopp(); waehlenRef.current(aktivRef.current); } return; }
        case 'ArrowLeft': case 'Backspace': stopp(); zurueckRef.current(); return;
        case 'ContextMenu': case 'F10': return; // öffnet neu (Host)
        case 'Shift': case 'Control': case 'Alt': case 'Meta': return;
        default: schliessen(); // Tippen, Tab …: Menü zu, Taste wirkt normal
      }
    };
    const daneben = (e: PointerEvent) => { if (!ref.current?.contains(e.target as Node)) schliessen(); };
    const scroll = (e: Event) => { if (!ref.current?.contains(e.target as Node)) schliessen(); };
    window.addEventListener('keydown', taste, true);
    document.addEventListener('pointerdown', daneben, true);
    window.addEventListener('scroll', scroll, true);
    window.addEventListener('wheel', scroll, { capture: true, passive: true });
    window.addEventListener('resize', schliessen);
    window.addEventListener('blur', schliessen);
    window.addEventListener('popstate', schliessen);
    return () => {
      window.removeEventListener('keydown', taste, true);
      document.removeEventListener('pointerdown', daneben, true);
      window.removeEventListener('scroll', scroll, true);
      window.removeEventListener('wheel', scroll, { capture: true } as EventListenerOptions);
      window.removeEventListener('resize', schliessen);
      window.removeEventListener('blur', schliessen);
      window.removeEventListener('popstate', schliessen);
    };
  }, []);
  // Aktiven Eintrag sichtbar halten (lange Menüs scrollen)
  useEffect(() => { ref.current?.querySelector('.km-eintrag.aktiv')?.scrollIntoView({ block: 'nearest' }); }, [aktiv]);

  return (
    <div ref={ref} className="kontextmenue" role="menu" aria-label={ebene.titel ?? 'Kontextmenü'} data-testid="kontextmenue"
      style={pos ? { left: pos.left, top: pos.top, maxHeight: pos.maxHoehe } : { left: offen.x, top: offen.y, visibility: 'hidden' }}
      onContextMenu={(e) => e.preventDefault()}>
      {zeilen.map((z, i) => z === '-'
        ? <div key={'t' + i} className="km-trenner" role="separator" />
        : (
          <button key={i} type="button" role="menuitem" tabIndex={-1} disabled={z.deaktiviert} aria-haspopup={z.unter ? 'menu' : undefined}
            className={'km-eintrag' + (i === aktiv ? ' aktiv' : '') + (z.gefahr ? ' gefahr' : '') + (ebene.titel && i === 0 ? ' zurueck' : '')}
            data-testid={z.testid ?? kennung(z.text)}
            // Fokus bleibt, wo er war (Textfeld behält Auswahl)
            onMouseDown={(e) => e.preventDefault()} onMouseEnter={() => !z.deaktiviert && setAktiv(i)} onClick={() => waehlen(i)}>
            <span className="km-icon">{z.gewaehlt ? <Icon name="haken" size={15} /> : z.icon ? <Icon name={z.icon} size={15} /> : null}</span>
            <span className="km-text">{z.text}</span>
            {z.kuerzel && <kbd>{z.kuerzel}</kbd>}
            {z.unter && <span className="km-pfeil"><Icon name="chevron" size={14} /></span>}
          </button>
        ))}
    </div>
  );
}
